import type { Tone } from './spec/types'

/**
 * Design fixtures — the sample incidents and commands the prototype ships with.
 *
 * NONE OF THIS IS LIVE DATA and the console says so ON THE SCREEN THAT DRAWS
 * IT, not once for the whole shell: the marker moved out of the cell bar into
 * `ScreenHeader` the moment the first screen went live, because a global claim
 * is false in one direction or the other as soon as the two kinds of screen
 * coexist. See the comment at the top of `shell/CellBar.tsx`.
 *
 * Four screens read `/v1/admin` today — `dashboard`, `rk-aml`, `rc-cand`,
 * `onb-sla`, the whole list being `SCREEN_LOADERS` in `src/lib/data`. The
 * other 111 draw this corpus and badge themselves as corpus.
 *
 * A count an operator believes is a count an operator acts on. This file is
 * deleted screen by screen as each one gets a real endpoint, never adapted:
 * a fixture edited to look like a response is how fake data survives.
 */

export interface Incident {
  readonly sev: string
  readonly tone: Tone
  readonly title: string
  readonly body: string
  readonly ref: string
}

/**
 * The good state is not an empty list — it is one open low-severity item, and
 * that item is a reconciler that has never produced a row.
 *
 * "Never ran" is deliberately NOT rendered as green. A reconciler with zero
 * runs has proven nothing; treating absence of failure as success is the exact
 * mistake this row exists to prevent (CLAUDE.md §2, law 19).
 */
export const CALM_INCIDENTS: readonly Incident[] = [
  {
    sev: 'Low',
    tone: 'n',
    title: 'movement_drift reconciler never produced a row',
    body: 'Kind registered 2026-07-24, zero runs since. This is not a clean run — it is an unproven reconciler.',
    ref: 'reconciliation_runs kind=movement_drift · rows=0',
  },
]

/**
 * What a degraded provider looks like. Note what is NOT here: an outage.
 *
 * A deep outbox and a lagging reconciler are STALE REPORTING (law 19). Money
 * writes are unaffected, readiness does not read these numbers, and saying
 * otherwise would train operators to page for the wrong thing.
 */
export const DEGRADED_INCIDENTS: readonly Incident[] = [
  {
    sev: 'High',
    tone: 'r',
    title: 'FinXP SEPA Instant degraded',
    body: 'Provider health check failing since 13:41 UTC. Inbound SCT Inst confirmations delayed; routing failed over to sepa (T+1).',
    ref: 'provider_events lag 27m · provider=finxp method=sepa_inst',
  },
  {
    sev: 'Med',
    tone: 'a',
    title: 'Notification outbox lag 24m',
    body: 'Outbox depth 8,412. Reports and notifications are stale. Money writes are unaffected and not gated on the outbox.',
    ref: 'worker=notification_dispatcher · claimable=8412 in_flight=64',
  },
  {
    sev: 'Med',
    tone: 'a',
    title: 'Balance-drift reconciler last success 02:10 UTC',
    body: 'Drift figures on Balances vs provider are stale reporting, not an outage.',
    ref: 'reconciliation_runs kind=balance_drift',
  },
]

/**
 * The note the console pushes to the top of any analytics screen while the
 * provider is degraded, so a stale figure is never read as a current one.
 */
export const DEGRADED_NOTE = {
  tone: 'a' as const,
  kicker: 'Degraded data',
  title: 'Analytics is 24 minutes behind — figures on this screen are stale',
  body: 'The notification outbox is deep (8,412 rows), so the analytics sink is lagging. This is stale reporting, not an outage: ledger-sourced screens are unaffected, no money write is blocked, and readiness does not read this number.',
  ref: 'outbox lag 24m · last materialization 2026-07-31 13:23 UTC',
}

/*
 * `ENTITIES` — what global search resolved — was here and is deleted (#30).
 *
 * It backed the palette's **Entities** group: ten invented records, among them
 * a customer with a `customers.id`, an email address and an IBAN. Those were
 * returned as SEARCH RESULTS, to a search the masthead advertises for exactly
 * those identifiers — so the console answered "yes, that IBAN is here" about
 * an account nobody had looked up, and the row navigated to Customer 360 as
 * though it had resolved to a record. A badge in the corner is ambient; a
 * result is a direct answer to a question an operator asked, and a hover
 * tooltip cannot fix a row that is about to be opened with Enter.
 *
 * There is no `GET /v1/admin/search`. Until there is, the palette renders no
 * Entities group at all — see `buildPalette` in `Console.tsx`. An empty result
 * for a customer who exists is a mild bug; a fabricated one for a customer who
 * does not is how someone gets told the wrong thing about a real person.
 *
 * Restore from that endpoint, never from a constant here.
 */

/**
 * Palette commands — `[label, screen or null, why it is blocked]`.
 *
 * The blocked entries are the interesting ones. Posting a correction,
 * publishing a price list and restricting a customer are all reachable in the
 * console, and none of them is reachable FROM HERE: each needs its screen's
 * context — the journal row, the diff, the customer — before it can be
 * authorised. A money-shaped action that can be fired from a search box is an
 * action fired without the thing it acts on in view.
 */
export const COMMANDS: readonly (readonly [
  label: string, screen: string | null, blocked: string,
])[] = [
  ['Open candidate queue', 'rc-cand', ''],
  ['New AML escalation', 'rk-aml', ''],
  ['Look up decision_ref', 'rk-decisions', ''],
  ['Open match simulator', 'rc-sim', ''],
  ['Toggle theme', null, ''],
  ['Post correction', null, 'needs a journal row in context'],
  ['Publish price list', null, 'needs the editor and its diff'],
  ['Set restricted', null, 'needs the customer in context'],
]

/**
 * The clock the design is drawn against.
 *
 * Fixed, not `new Date()`. Every timestamp in the corpus is relative to this
 * instant — "4m old", "19h against a 24h SLA", "raised 2026-07-29" — so a live
 * clock would put the screen's own prose out of step with its own figures.
 * Replaced by a real clock when the data is real.
 */
export const FIXTURE_CLOCK = '2026-07-31 14:02 UTC'
